<template>
  <div
    v-if="recording"
    class="text-center"
  >
    <!-- <div v-if="recording.playbackUrl && !resultsLoaded"  -->
    <div
      v-if="recording && recording.playbackUrl && !resultsLoaded"
      class="text-center"
    >
      <h2 v-if="scores">
        <svg
          class="icon-refresh rotate large-spinner"
        >
          <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
        </svg> Loading results...
      </h2>
      <div
        v-if="!scores"
        class="text-center"
      >
        <div v-if="recording.failed && recording.failedReason == 'No speech detected for 30s.'">
          <h2>
            <svg class="icon-alert-triangle">
              <use xlink:href="/static/svg-symbols.svg#icon-alert-triangle" />
            </svg>
            Extended Silence Detected in Recording
          </h2>
          <p style="text-align: left;">
            VoiceVibes detected a period of at least 30 seconds in which no one was speaking.
            These extended silences prevent our metrics from being calculated, so results for
            this recording cannot be displayed.
            <br><br>
            <span class="sr-only">
              Please make a note of where these extended silences occur. You can then upload your
              recording again and use the trim controls in VoiceVibes to create one or more recordings
              which do not contain extended silences and will successfully process.
            </span>
          </p>
        </div>
        <div v-else-if="recording.failed && !recording.duration">
          <h2>
            <svg class="icon-alert-triangle">
              <use xlink:href="/static/svg-symbols.svg#icon-alert-triangle" />
            </svg>
            Your Recording Contains No Data
          </h2>
          <p style="text-align: left;">
            The uploaded recording does not appear to contain any audio data. If you used the
            trim controls to select a time range, it is possible you selected a time range
            that does not exist for the actual recording (For example, selecting
            10:00 through 12:00 in a recording that is only 5 minutes long).
            Alternately, you may be having issues with your device's audio capture settings.
            <br><br>
            <span class="evaluate-label">
              To fix your recording problems, please review
              <a
                target="_blank"
                href="https://voicevibes.s3-us-west-2.amazonaws.com/resources/Troubleshooting-Recording-Errors-in-VoiceVibes.pdf"
              >
                Troubleshooting Recording Errors in VoiceVibes
              </a>
            </span>
          </p>
        </div>
        <div v-else-if="recording.failed && recording.duration >= 5">
          <h2>
            <svg class="icon-alert-triangle">
              <use xlink:href="/static/svg-symbols.svg#icon-alert-triangle" />
            </svg>
            Your Audio Could Not be Processed
          </h2>
          <p style="text-align: left;">
            Is your microphone muted or not accessible? This recording does not contain valid audio data --
            either because it is not a supported audio or video file, or because it
            does not contain any instances of someone speaking. You may need to adjust your microphone
            settings to ensure your audio capture works properly.
            <br><br>
            <span class="evaluate-label">
              To fix your recording problems, please review
              <a
                target="_blank"
                href="https://voicevibes.s3-us-west-2.amazonaws.com/resources/Troubleshooting-Recording-Errors-in-VoiceVibes.pdf"
              >
                Troubleshooting Recording Errors in VoiceVibes
              </a>
            </span>
          </p>
        </div>
        <div v-else-if="recording.failed && recording.duration < 5">
          <h2>
            <svg class="icon-alert-triangle">
              <use xlink:href="/static/svg-symbols.svg#icon-alert-triangle" />
            </svg>
            Your Recording is Too Short
          </h2>
          <p style="text-align: left;">
            Recordings shorter than 5 seconds long cannot be processed.
            Please try again with a longer recording.
          </p>
        </div>
        <h2 v-else-if="recording && !recording.failed && recording.playbackUrl">
          <svg
            class="icon-refresh rotate large-spinner"
          >
            <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
          </svg> Computing results...
        </h2>
      </div>
    </div>
    <h2 v-else-if="recording.failed">
      <!-- Catch All for Failures -->
      <svg class="icon-alert-triangle">
        <use xlink:href="/static/svg-symbols.svg#icon-alert-triangle" />
      </svg>
      An error occurred: {{ recording.failedReason }}<br>
      <div
        v-if="recording.failedReason === 'Recording could not be processed. Do not retry.'"
      >
        If you tried to process a specific file or URL, then most likely the associated file/URL is not
        a valid audio or video file which VoiceVibes is able to access and analyze.
      </div>
      <div
        v-if="recording.fileName && recording.fileName.indexOf('zoom.us') !== -1"
        style="font-size: 13px; text-align: left;"
      >
        <br>
        <span>
          The VoiceVibes Zoom integration encountered an error while attempting to retrieve your recording
          from Zoom. Please retry your request and if it fails again, consider logging in to your
          Zoom account to download your recording directly from Zoom. You can then use the VoiceVibes
          Upload Tab on the RECORD page to upload your recording.
          <br><br>
          We apologize for any inconvenience. You may also wish to contact
          VoiceVibes support via
          <a href="mailto:support@myvoicevibes.com">
            support@myvoicevibes.com
          </a>
          if the error persists.
        </span>
      </div>
    </h2>
    <h2 v-else-if="recording.uploading">
      <svg
        class="icon-refresh rotate large-spinner"
      >
        <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
      </svg> Audio is still being uploaded...
    </h2>
    <h2 v-else-if="recording && recording.processing && !recording.playbackUrl">
      <svg
        class="icon-refresh rotate large-spinner"
      >
        <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
      </svg>
      <span id="extractingAudio">
        Extracting audio...
      </span>
    </h2>
  </div>
</template>

<style lang="less" scoped>
  .icon-alert-triangle {
    width: 25px; height: 25px;
  }
</style>

<script>
export default {
  props: ['recording'],
  data() {
    return {};
  },
  computed: {
    scores: {
      cache: false,
      get() {
        return this.recording.results.scores;
      }
    },
    tips: {
      cache: false,
      get() {
        return this.recording.results.tips;
      }
    },
    timestamped: {
      cache: false,
      get() {
        return this.recording.results.timestamped;
      }
    },
    resultsLoaded: {
      cache: false,
      get() {
        return (this.recording && this.recording.results && this.scores
            && this.tips
            && this.timestamped) || (this.recording && this.recording.results && this.recording.results.data);
      }
    },
  }
};

</script>